import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { createTableHook, tableFeatures } from "@tanstack/react-table";
import { Button } from "@astryxdesign/core/Button";
import * as stylex from "@stylexjs/stylex";
import { queries, errorMessage } from "../lib/api";
import { styles } from "../lib/styles";
import type { Recording } from "../lib/schemas";
import { RecordingPlayer } from "../components/RecordingPlayer";

export const Route = createFileRoute("/recordings")({ component: RecordingsPage });
const { useAppTable, createAppColumnHelper } = createTableHook({ features: tableFeatures({}) });
const helper = createAppColumnHelper<Recording>();
function RecordingsPage() {
  const me = useQuery(queries.me());
  const list = useQuery({ ...queries.recordings(), enabled: me.data?.admin === true });
  const [selected, setSelected] = useState(""),
    [filter, setFilter] = useState("");
  const preview = useQuery({
    ...queries.recording(selected),
    enabled: me.data?.admin === true && selected !== "",
  });
  const columns = useMemo(
    () => [
      helper.display({
        id: "when",
        header: "Started",
        cell: ({ row }) => new Date(row.original.created_at).toLocaleString(),
      }),
      helper.display({
        id: "server",
        header: "Server / account",
        cell: ({ row }) => `${row.original.alias} / ${row.original.login}`,
      }),
      helper.display({ id: "who", header: "Person", cell: ({ row }) => row.original.email }),
      helper.display({ id: "kind", header: "Channel", cell: ({ row }) => row.original.kind }),
      helper.display({
        id: "state",
        header: "State",
        cell: ({ row }) => (
          <span
            {...stylex.props(styles.status, row.original.status === "complete" && styles.applied)}
          >
            {row.original.status}
          </span>
        ),
      }),
      helper.display({
        id: "action",
        header: "Review",
        cell: ({ row }) => (
          <Button
            label="Replay"
            size="sm"
            isDisabled={row.original.status === "recording"}
            onClick={() => setSelected(row.original.id)}
          />
        ),
      }),
    ],
    [],
  );
  const data = (list.data?.items ?? []).filter((item) =>
    `${item.alias} ${item.email} ${item.login} ${item.kind}`
      .toLowerCase()
      .includes(filter.toLowerCase()),
  );
  const table = useAppTable({ columns, data });
  if (!me.data?.admin)
    return (
      <p role="alert" {...stylex.props(styles.error)}>
        Administrator access is required.
      </p>
    );
  return (
    <>
      <div {...stylex.props(styles.pageHeading)}>
        <div>
          <h1 {...stylex.props(styles.title)}>Session recordings</h1>
          <p {...stylex.props(styles.description)}>
            Review managed SSH output and command requests. Recordings are encrypted and expire
            after 30 days. Forwarded protocols and SFTP have activity metadata.
          </p>
        </div>
      </div>
      {list.data && !list.data.storage.healthy && (
        <p role="alert" {...stylex.props(styles.error)}>
          Recording storage needs attention. New managed SSH access is blocked until it is healthy.
        </p>
      )}
      {(list.error || preview.error) && (
        <div role="alert" {...stylex.props(styles.error)}>
          {errorMessage(list.error || preview.error)}{" "}
          <a href="/auth/login">Refresh administrator sign-in</a>
        </div>
      )}
      {preview.data && <RecordingPlayer key={preview.data.recording.id} preview={preview.data} />}
      <section {...stylex.props(styles.panel)}>
        <label {...stylex.props(styles.field)}>
          Search recordings
          <input
            {...stylex.props(styles.select)}
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Server, person, account or channel"
          />
        </label>
        <div {...stylex.props(styles.tableWrap)}>
          <table {...stylex.props(styles.table)}>
            <thead>
              {table.getHeaderGroups().map((group) => (
                <tr key={group.id}>
                  {group.headers.map((header) => (
                    <th key={header.id} {...stylex.props(styles.th)}>
                      <table.FlexRender header={header} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getAllCells().map((cell) => (
                    <td key={cell.id} {...stylex.props(styles.td)}>
                      <table.FlexRender cell={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!list.isPending && data.length === 0 && (
          <p {...stylex.props(styles.empty)}>No matching recordings within the retention window.</p>
        )}
        {list.isPending && <p {...stylex.props(styles.empty)}>Loading recordings…</p>}
        <p {...stylex.props(styles.small)}>
          Showing up to 200 recent recordings. Replay and downloads are audited and require a recent
          administrator sign-in.
        </p>
      </section>
    </>
  );
}
